import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IFilterAngularComp } from 'ag-grid-angular';
import {
  IAfterGuiAttachedParams,
  IDoesFilterPassParams,
  IFilterParams,
} from 'ag-grid-community';

/** ag-grid's own text operators; the model is the one agTextColumnFilter emits. */
export type TextFilterOperator =
  | 'contains'
  | 'notContains'
  | 'equals'
  | 'notEqual'
  | 'startsWith'
  | 'endsWith'
  | 'blank'
  | 'notBlank';

export interface TextFilterModel {
  filterType: 'text';
  type: TextFilterOperator;
  filter?: string;
}

const ALL_OPERATORS: readonly TextFilterOperator[] = [
  'contains',
  'notContains',
  'equals',
  'notEqual',
  'startsWith',
  'endsWith',
  'blank',
  'notBlank',
];

const NO_VALUE_OPERATORS: ReadonlySet<TextFilterOperator> = new Set([
  'blank',
  'notBlank',
]);

const OPERATOR_LABEL_KEYS: Record<TextFilterOperator, string> = {
  contains: 'ag-text-filter.option.contains',
  notContains: 'ag-text-filter.option.not-contains',
  equals: 'ag-text-filter.option.equals',
  notEqual: 'ag-text-filter.option.not-equal',
  startsWith: 'ag-text-filter.option.starts-with',
  endsWith: 'ag-text-filter.option.ends-with',
  blank: 'ag-text-filter.option.blank',
  notBlank: 'ag-text-filter.option.not-blank',
};

/**
 * The text column filter popup, in the app's own controls (WAP-1646). One
 * condition: an operator and, unless the operator is Blank / Not blank, a
 * value. Emits the standard agTextColumnFilter model so SSRM and the backend
 * translators are untouched. Honours `filterParams.filterOptions` (allowed
 * operators) and `filterParams.defaultOption`, like the built-in filter.
 * The column normaliser (filter-buttons.utils.ts) installs it grid-wide.
 */
@Component({
  selector: 'app-ag-text-filter',
  templateUrl: './ag-text-filter.component.html',
  styleUrls: ['./ag-text-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AgTextFilterComponent implements IFilterAngularComp {
  private static nextId = 0;
  private readonly translate = inject(TranslateService);
  private params!: IFilterParams;
  private hidePopup: (() => void) | null = null;
  private applied: TextFilterModel | null = null;
  private defaultOperator: TextFilterOperator = 'contains';

  readonly uid = `ag-text-filter-${AgTextFilterComponent.nextId++}`;
  operators: readonly TextFilterOperator[] = ALL_OPERATORS;
  /** Pending selection shown in the popup; committed on Apply. */
  operator: TextFilterOperator = 'contains';
  text = '';

  agInit(params: IFilterParams): void {
    this.params = params;
    const filterParams = (params.colDef?.filterParams ?? {}) as {
      filterOptions?: unknown[];
      defaultOption?: unknown;
    };
    const allowed = (filterParams.filterOptions ?? []).filter(
      (option): option is TextFilterOperator =>
        typeof option === 'string' &&
        ALL_OPERATORS.includes(option as TextFilterOperator),
    );
    this.operators = allowed.length > 0 ? allowed : ALL_OPERATORS;
    const preferred = filterParams.defaultOption as TextFilterOperator;
    this.defaultOperator = this.operators.includes(preferred)
      ? preferred
      : this.operators[0];
    this.operator = this.defaultOperator;
  }

  get needsValue(): boolean {
    return !NO_VALUE_OPERATORS.has(this.operator);
  }

  get canApply(): boolean {
    return !this.needsValue || this.text.trim() !== '';
  }

  isFilterActive(): boolean {
    return this.applied != null;
  }

  // * Only client-side grids call this; SSRM sends the model to the backend.
  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const model = this.applied;
    if (!model) return true;
    const raw = this.params.getValue(params.node);
    const value = raw == null ? '' : String(raw).toLowerCase();
    const needle = (model.filter ?? '').toLowerCase();
    let pass: boolean;
    switch (model.type) {
      case 'contains':
        pass = value.includes(needle);
        break;
      case 'notContains':
        pass = !value.includes(needle);
        break;
      case 'equals':
        pass = value === needle;
        break;
      case 'notEqual':
        pass = value !== needle;
        break;
      case 'startsWith':
        pass = value.startsWith(needle);
        break;
      case 'endsWith':
        pass = value.endsWith(needle);
        break;
      case 'blank':
        pass = value === '';
        break;
      case 'notBlank':
        pass = value !== '';
        break;
    }
    return pass;
  }

  getModel(): TextFilterModel | null {
    return this.applied;
  }

  setModel(model: TextFilterModel | null): void {
    const type = model?.type;
    if (type && this.operators.includes(type)) {
      const value = (model?.filter ?? '').trim();
      this.applied =
        NO_VALUE_OPERATORS.has(type) || value !== ''
          ? {
              filterType: 'text',
              type,
              ...(NO_VALUE_OPERATORS.has(type) ? {} : { filter: value }),
            }
          : null;
    } else {
      this.applied = null;
    }
    this.syncPendingFromApplied();
  }

  /** Read-only text shown in the floating filter row. */
  getModelAsString(): string {
    const model = this.applied;
    let text = '';
    if (model) {
      text = this.operatorLabel(model.type);
      if (!NO_VALUE_OPERATORS.has(model.type)) {
        text += `: ${model.filter ?? ''}`;
      }
    }
    return text;
  }

  afterGuiAttached(params?: IAfterGuiAttachedParams): void {
    this.hidePopup = params?.hidePopup ?? null;
    // Reopening discards any uncommitted edits from the previous visit.
    this.syncPendingFromApplied();
  }

  operatorLabel(operator: TextFilterOperator): string {
    return this.translate.instant(OPERATOR_LABEL_KEYS[operator]);
  }

  onOperatorChange(operator: TextFilterOperator): void {
    this.operator = operator;
    if (!this.needsValue) {
      this.text = '';
    }
  }

  apply(): void {
    if (!this.canApply) return;
    this.applied = this.needsValue
      ? { filterType: 'text', type: this.operator, filter: this.text.trim() }
      : { filterType: 'text', type: this.operator };
    this.params.filterChangedCallback();
    this.hidePopup?.();
  }

  /**
   * Header-row input (AgTextFloatingFilterComponent): the typed text with the
   * current value operator, or the default one when the popup holds Blank /
   * Not blank; empty text clears the filter.
   */
  applyFromFloating(text: string): void {
    const value = text.trim();
    const operator = NO_VALUE_OPERATORS.has(this.operator)
      ? this.defaultOperator
      : this.operator;
    this.applied =
      value === ''
        ? null
        : { filterType: 'text', type: operator, filter: value };
    this.syncPendingFromApplied();
    this.params.filterChangedCallback();
  }

  /** Cancel / Escape: drop the uncommitted edits and close; the applied filter stays. */
  cancel(): void {
    this.syncPendingFromApplied();
    this.hidePopup?.();
  }

  reset(): void {
    const wasActive = this.isFilterActive();
    this.applied = null;
    this.operator = this.defaultOperator;
    this.text = '';
    if (wasActive) {
      this.params.filterChangedCallback();
    }
    this.hidePopup?.();
  }

  private syncPendingFromApplied(): void {
    this.operator = this.applied?.type ?? this.defaultOperator;
    this.text = this.applied?.filter ?? '';
  }
}
